/**
 * Per-panel-type column catalog formatter for AI tool results.
 *
 * The AI doesn't get column catalogs in the system prompt — they're delivered
 * on demand via tool results: add_panel returns the new panel's catalog,
 * set_filters returns it inline when a column key is invalid.
 *
 * Each panel type maps to its own column-defs source. Scanner, multi-scanner,
 * watchlist, key-stats, the heatmap, multi-chart and theme tracker share the
 * scanner catalog (they grid or scan the same equity rows); News carries its own
 * curated subset of it. Calendar has its own; Ticker Stats, the History panels
 * and Tag Explorer read the history catalog. Panels without a column catalog
 * (chart, bar-chart, notebook) return ''.
 */

import type { ColumnCatalogDef } from '@/types/column-catalog'
import { SCANNER_COLUMN_CATALOG } from '@/features/scanner/schemas/scanner-column-defs'
import { CALENDAR_COLUMN_CATALOG } from '@/features/calendar/schemas/calendar-column-defs'
import { NEWS_COLUMN_CATALOG } from '@/features/news/schemas/news-column-defs'
import {
  HISTORY_COLUMN_CATALOG,
  HISTORY_COLUMN_MAP,
} from '@/features/ticker-stats/schemas/history-column-defs'
import { OPERATORS_BY_DATA_TYPE } from '@/features/sliced-form/schemas/filter-entity'

/**
 * Operator help shown to the AI, GENERATED from the canonical
 * `OPERATORS_BY_DATA_TYPE` (the same table the filter validator enforces) so the
 * model can never be told an operator that validation would then reject. Data
 * types that share an identical operator list collapse onto one line.
 */
function buildFilterOpsHelp(): string {
  const byOps = new Map<string, string[]>() // "op, op, …" → [dataType, …]
  for (const [dataType, ops] of Object.entries(OPERATORS_BY_DATA_TYPE)) {
    const key = ops.join(', ')
    byOps.set(key, [...(byOps.get(key) ?? []), dataType])
  }
  const lines = ['Filter operators by data type:']
  for (const [ops, dataTypes] of byOps) lines.push(`- ${dataTypes.join('/')}: ${ops}`)
  return lines.join('\n')
}

const FILTER_OPS_HELP = buildFilterOpsHelp()

/**
 * Temporary cap on which categories of the scanner catalog get shipped in tool
 * results. The full SCANNER_COLUMN_CATALOG has ~146 columns — too noisy for the
 * model and the prompt budget while we're still iterating. Once a pgvector
 * column-retrieval tool lands, this whitelist goes away in favor of "top-N
 * columns relevant to the user's request."
 */
const SCANNER_PREVIEW_CATEGORIES = new Set(['Commonly Used', 'Share Structure'])

/**
 * Same cap for the history catalog. The full HISTORY_COLUMN_CATALOG is ~1000+
 * columns and its `category` field is coarse — nearly everything lives under a
 * single 'Market Data' bucket (the fine structure is in each column's `group`).
 * Until column retrieval lands we ship only the grouping / discriminative
 * dimensions a trader reaches for first: 'Company' (ticker, sector, industry,
 * exchange, country — the group_by axes) and 'Share Structure' (float, market
 * cap, insider/institution %). 'Market Data', 'Tags', and 'Halts' are held back.
 */
const HISTORY_PREVIEW_CATEGORIES = new Set(['Company', 'Share Structure'])

/** Group columns by their `category` field, preserving insertion order. */
function groupByCategory(catalog: ColumnCatalogDef[]): Map<string, ColumnCatalogDef[]> {
  const groups = new Map<string, ColumnCatalogDef[]>()
  for (const col of catalog) {
    const category = col.category || 'Other'
    const list = groups.get(category) ?? []
    list.push(col)
    groups.set(category, list)
  }
  return groups
}

/** Render one column row as `- key — label (dataType[, enum: a, b, c])`. */
function formatRow(col: ColumnCatalogDef): string {
  const label = col.formLabel || col.label
  let typeStr: string = col.dataType
  if (col.dataType === 'enum' && col.enumOptions && col.enumOptions.length > 0) {
    const opts = col.enumOptions.map((o) => o.value).join(', ')
    typeStr = `enum: ${opts}`
  }
  return `- ${col.key} — ${label} (${typeStr})`
}

/** Render an entire catalog as a system-prompt-style markdown block. */
function formatCatalog(title: string, catalog: ColumnCatalogDef[]): string {
  const lines: string[] = [`## ${title}`, '']
  for (const [category, cols] of groupByCategory(catalog)) {
    lines.push(`**${category}:**`)
    for (const col of cols) lines.push(formatRow(col))
    lines.push('')
  }
  lines.push('Use these keys exactly when calling set_filters or set_columns.')
  lines.push(FILTER_OPS_HELP)
  return lines.join('\n')
}

/**
 * Returns the column catalog text for a panel type, or '' if the panel has
 * no filterable/configurable column catalog (e.g. chart, bar-chart).
 */
export function getColumnCatalogText(panelType: string): string {
  switch (panelType) {
    case 'scanner':
    case 'multi-scanner':
    case 'watchlist':
    case 'key-stats':
    case 'sector-heatmap':
    case 'multi-chart':
    case 'theme-tracker':
    case 'alert-windows': {
      const subset = SCANNER_COLUMN_CATALOG.filter((c) =>
        SCANNER_PREVIEW_CATEGORIES.has(c.category),
      )
      return formatCatalog('available column keys (preview subset)', subset)
    }
    case 'news':
      return formatCatalog('available news column keys', NEWS_COLUMN_CATALOG)
    case 'calendar':
      return formatCatalog('available calendar column keys', CALENDAR_COLUMN_CATALOG)
    case 'history-records':
    case 'history-stats':
    case 'history-time-series':
    case 'history-scatter':
    case 'history-bar':
    case 'history-bar-category':
    case 'history-intraday':
    case 'history-calendar':
    case 'history-sankey':
    case 'history-comparison':
    case 'history-news':
    case 'ticker-stats':
    case 'tags-explorer': {
      const subset = HISTORY_COLUMN_CATALOG.filter((c) =>
        HISTORY_PREVIEW_CATEGORIES.has(c.category),
      )
      return formatCatalog('available history column keys (preview subset)', subset)
    }
    default:
      return ''
  }
}

/** Shape of a history column, for the deterministic chart chooser (spec D3). */
export interface HistoryColumnShape {
  dataType: string
  enumOptions?: string[]
  enumCardinality?: number
}

/**
 * Look up a history column's shape (data type + enum option list/cardinality).
 * Used by `use-history-action-callbacks.ts` when building chart suggestions.
 * Returns null for unknown keys. `enumCardinality` is only meaningful for enum
 * columns — for other data types the chooser infers cardinality from the query
 * result, not the catalog.
 */
export function getHistoryColumnShape(column: string): HistoryColumnShape | null {
  const def = HISTORY_COLUMN_MAP[column]
  if (!def) return null
  const shape: HistoryColumnShape = { dataType: def.dataType }
  if (def.enumOptions && def.enumOptions.length > 0) {
    shape.enumOptions = def.enumOptions.map((o) => o.value)
    shape.enumCardinality = def.enumOptions.length
  }
  return shape
}

/**
 * True when a history column may be used as a filter criterion. `displayOnly`
 * columns (the relative dates, `day1_date`, `day_m1_date`, ...) can be shown in
 * a grid but are never filtered on, in the UI or by the AI's set_filters.
 */
export function isHistoryFilterColumn(column: string): boolean {
  const def = HISTORY_COLUMN_MAP[column]
  return def != null && !def.displayOnly
}
